import {
	Add,
	ArrowDownward,
	ArrowUpward,
	Delete,
	EditNotifications,
	Restore,
	Save,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
	Detail,
	DetailNotification,
	Person,
	QueryStatus,
	SessionState,
	SessionStatus,
	getDetails,
	getNotifications,
	postAction,
	removeDuplicates,
	stringArraysEqual,
} from "../utils";

export enum DetailsType {
	Note,
	Task,
	Deadline,
}

function createDetailEmails(
	assignees: string[],
	attorneys: string[],
	staffs: string[],
	people: Person[],
	clientName: string,
	description: string,
	type: DetailsType,
	date: Dayjs | null
) {
	switch (type) {
		case DetailsType.Task:
			return removeDuplicates([...assignees])
				.filter(
					(assignee) =>
						people.find(({ _id }) => _id === assignee)?.email !==
						undefined
				)
				.map((assignee) => ({
					subject: "Assigned New Task",
					text: `Dear ${
						people.find(({ _id }) => _id === assignee)?.name
					}\n\nThe following task was assigned to you on ${clientName}\n\n${description}`,
					toEmails: [
						people.find(({ _id }) => _id === assignee)?.email,
					],
				}));
		case DetailsType.Deadline:
			return [
				...removeDuplicates(attorneys)
					.filter(
						(assignee) =>
							people.find(({ _id }) => _id === assignee)
								?.email !== undefined
					)
					.map((attorney) => ({
						subject: "Assigned New Deadline",
						text: `Dear ${
							people.find(({ _id }) => _id === attorney)?.name
						}\n\nThe following deadline ${
							date !== null
								? `(${date.format("M/D/YYYY h:mm A")}) `
								: ""
						}was assigned to you on ${clientName}\n\n${description}`,
						toEmails: [
							people.find(({ _id }) => _id === attorney)?.email,
						],
					})),
				...removeDuplicates([...assignees, ...staffs])
					.filter(
						(assignee) =>
							people.find(({ _id }) => _id === assignee)
								?.email !== undefined
					)
					.map((assignee) => ({
						subject: "Assigned New Deadline",
						text: `Dear ${
							people.find(({ _id }) => _id === assignee)?.name
						}\n\nThe following deadline ${
							date !== null
								? `(${date.format("M/D/YYYY h:mm A")}) `
								: ""
						}was assigned to you on ${clientName}\n\n${description}\n\nPlease make sure this deadline is added to the following attorney's calendar:\n\n${attorneys
							.map(
								(a) =>
									people.find(({ _id }) => _id === a)?.name ||
									a
							)
							.join("\n")}`,
						toEmails: [
							people.find(({ _id }) => _id === assignee)?.email,
						],
					})),
			];
	}

	return [];
}

interface DetailsNotificationsRowProps {
	notification: DetailNotification;
	refetchNotifications: () => void;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setPostStatus: (arg0: QueryStatus) => void;
	setInternalRemindersEdited: (arg0: boolean) => void;
}

function DetailsNotificationsRow({
	notification,
	refetchNotifications,
	sessionState,
	setSessionState,
	setPostStatus,
	setInternalRemindersEdited,
}: DetailsNotificationsRowProps) {
	const [dayOffset, setDayOffset] = useState(notification.dayOffset);

	const isEdited = dayOffset !== notification.dayOffset;

	useEffect(() => setInternalRemindersEdited(isEdited), [isEdited]);

	return (
		<Box
			sx={{
				display: "flex",
				borderBottom: (theme) => `1px solid ${theme.palette.grey[800]}`,
				borderLeft: isEdited ? "3px solid #005593" : 0,
				paddingLeft: isEdited ? 0 : "3px",
				alignItems: "center",
			}}
		>
			<Typography sx={{ p: 1 }} color="white">
				Send reminder at
			</Typography>
			<Select
				sx={{ m: 1 }}
				size="small"
				value={dayOffset}
				onChange={(e) => setDayOffset(Number(e.target.value))}
			>
				{Array(90)
					.fill(null)
					.map((_, i) => i + 1)
					.map((i) => (
						<MenuItem key={i} value={i}>
							<ListItemText primary={`${i} days`} />
						</MenuItem>
					))}
			</Select>
			<Typography sx={{ p: 1 }} color="white">
				prior
			</Typography>
			<Box>
				<Tooltip title="Save Changes">
					<IconButton
						sx={{ m: 1 }}
						disabled={!isEdited}
						onClick={() =>
							postAction(
								{
									action: "update-notification",
									id: notification._id,
									data: {
										...notification,
										_id: undefined,
										dayOffset,
									},
								},
								sessionState,
								setSessionState,
								() => {
									setPostStatus(QueryStatus.SUCCESS);
									refetchNotifications();
								},
								(e) => {
									console.error(e);
									setPostStatus(QueryStatus.FAILURE);
								}
							)
						}
					>
						<Save />
					</IconButton>
				</Tooltip>
				{isEdited ? (
					<Tooltip title="Undo Changes">
						<IconButton
							sx={{ m: 1 }}
							onClick={() => {
								setDayOffset(notification.dayOffset);
							}}
						>
							<Restore />
						</IconButton>
					</Tooltip>
				) : (
					<Tooltip title="Delete">
						<IconButton
							sx={{ m: 1 }}
							onClick={() =>
								postAction(
									{
										action: "delete-notification",
										id: notification._id,
									},
									sessionState,
									setSessionState,
									() => {
										setPostStatus(QueryStatus.SUCCESS);
										refetchNotifications();
									},
									(e) => {
										console.error(e);
										setPostStatus(QueryStatus.FAILURE);
									}
								)
							}
						>
							<Delete />
						</IconButton>
					</Tooltip>
				)}
			</Box>
		</Box>
	);
}

interface DetailsNotificationsProps {
	detail: Detail;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setPostStatus: (arg0: QueryStatus) => void;
	declareEdited: (arg0: string, arg1: boolean) => void;
	setRemindersEdited: (arg0: boolean) => void;
}

function DetailsNotifications({
	detail,
	sessionState,
	setSessionState,
	setPostStatus,
	declareEdited,
	setRemindersEdited,
}: DetailsNotificationsProps) {
	const [newDayOffset, setNewDayOffset] = useState<number | null>(null);
	const [internalRemindersEdited, setInternalRemindersEdited] =
		useState(false);

	const notificationsQuery = useQuery({
		queryKey: ["notifications", detail._id],
		queryFn: () =>
			getNotifications(
				{
					archived: false,
					detailId: detail._id,
				},
				sessionState,
				setSessionState
			),
		enabled: true,
	});

	const notifications: DetailNotification[] = notificationsQuery.data || [];

	const isEdited = newDayOffset !== null || internalRemindersEdited;

	useEffect(() => {
		declareEdited(`details-notification-${detail._id}`, isEdited);
		setRemindersEdited(isEdited);
	}, [isEdited]);

	return (
		<Box sx={{ p: 1, background: "#212121" }}>
			<Box
				sx={{
					borderRadius: "4px",
					border: (theme) => `1px solid ${theme.palette.grey[800]}`,
				}}
			>
				{notifications.map((notification) => (
					<DetailsNotificationsRow
						setInternalRemindersEdited={setInternalRemindersEdited}
						key={notification._id}
						notification={notification}
						sessionState={sessionState}
						setSessionState={setSessionState}
						setPostStatus={setPostStatus}
						refetchNotifications={notificationsQuery.refetch}
					/>
				))}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
					}}
				>
					<Select
						sx={{ m: 1 }}
						size="small"
						value={newDayOffset}
						onChange={(e) =>
							setNewDayOffset(Number(e.target.value))
						}
					>
						{Array(90)
							.fill(null)
							.map((_, i) => i + 1)
							.map((i) => (
								<MenuItem key={i} value={i}>
									<ListItemText primary={`${i} days`} />
								</MenuItem>
							))}
					</Select>
					<Tooltip title="Save Reminder">
						<IconButton
							sx={{ m: 1 }}
							disabled={newDayOffset === null}
							onClick={() =>
								postAction(
									{
										action: "add-notification",
										data: {
											detailId: detail._id,
											dayOffset: newDayOffset,
										},
									},
									sessionState,
									setSessionState,
									() => {
										setPostStatus(QueryStatus.SUCCESS);
										notificationsQuery.refetch();
										setNewDayOffset(null);
									},
									(e) => {
										console.error(e);
										setPostStatus(QueryStatus.FAILURE);
									}
								)
							}
						>
							<Add />
						</IconButton>
					</Tooltip>
					{isEdited && (
						<Tooltip title="Undo Changes">
							<IconButton
								sx={{ m: 1 }}
								onClick={() => {
									setNewDayOffset(null);
								}}
							>
								<Restore />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>
		</Box>
	);
}

interface DetailsRowProps {
	people: Person[];
	isLast: boolean;
	type: DetailsType;
	detail: Detail;
	saveKeyPressed: boolean;
	deleteDetail: (id: string) => void;
	updateDetail: (
		id: string,
		date: Dayjs,
		assignees: string[],
		description: string
	) => void;
	declareEdited: (arg0: string, arg1: boolean) => void;
	setIsComplete: (arg0: string, value: boolean) => void;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setPostStatus: (arg0: QueryStatus) => void;
}

function DetailsRow({
	people,
	isLast,
	type,
	detail,
	deleteDetail,
	updateDetail,
	declareEdited,
	setIsComplete,
	saveKeyPressed,
	sessionState,
	setSessionState,
	setPostStatus,
}: DetailsRowProps) {
	const [newDate, setNewDate] = useState(dayjs(detail.date));
	const [newAssignees, setNewAssignees] = useState(detail.assignees);
	const [newDescription, setNewDescription] = useState(detail.description);
	const [notificationsOpen, setNotificationsOpen] = useState(false);
	const [remindersEdited, setRemindersEdited] = useState(false);

	const isDateEdited = newDate.unix() !== dayjs(detail.date).unix();
	const isAssigneeEdited = !stringArraysEqual(newAssignees, detail.assignees);
	const isDescriptionEdited = newDescription !== detail.description;
	const isEdited = isDateEdited || isAssigneeEdited || isDescriptionEdited;

	const isSevenDaysOut = dayjs(detail.date).diff(dayjs(), "days") < 7;
	const isPassed = dayjs(detail.date).diff(dayjs(), "days") < -1;
	let background = "none";
	let hoverBackground = "#212121";
	if ([DetailsType.Task, DetailsType.Deadline].includes(type)) {
		if (isSevenDaysOut && !isPassed) {
			background = "#221111";
			hoverBackground = "#332222";
		}
	}

	useEffect(() => declareEdited(`details-${type}`, isEdited), [isEdited]);

	useEffect(() => {
		if (saveKeyPressed === true) {
			if (!isEdited) {
				return;
			}
			updateDetail(detail._id, newDate, newAssignees, newDescription);
		}
	}, [saveKeyPressed]);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					background: background,
					borderBottom: !isLast
						? (theme) => `1px solid ${theme.palette.grey[800]}`
						: "none",
					":hover": { background: hoverBackground },
					borderLeft: isEdited ? "3px solid #005593" : 0,
					paddingLeft: isEdited ? 0 : "3px",
				}}
			>
				{[DetailsType.Task, DetailsType.Deadline].includes(type) && (
					<Tooltip title="Mark as complete">
						<Checkbox
							sx={{ m: 1, marginRight: 0 }}
							size="medium"
							checked={detail?.isComplete}
							onChange={(_, value) =>
								setIsComplete(detail?._id, value)
							}
						/>
					</Tooltip>
				)}
				{[DetailsType.Deadline, DetailsType.Task].includes(type) && (
					<Tooltip title="Edit Reminders">
						<IconButton
							disabled={remindersEdited}
							sx={{ m: 1, marginLeft: 0 }}
							onClick={() =>
								setNotificationsOpen(!notificationsOpen)
							}
						>
							<EditNotifications />
						</IconButton>
					</Tooltip>
				)}
				<DateTimePicker
					// disabled={[DetailsType.Note].includes(type as DetailsType)}
					sx={{
						marginLeft: "5px",
						marginTop: 1,
						marginBottom: 1,
						marginRight: 1,
						width: "220px",
					}}
					slotProps={{
						textField: {
							size: "small",
							focused: isDateEdited,
							color: isDateEdited ? "secondary" : "primary",
						},
					}}
					value={newDate}
					onChange={(value) => {
						if (value !== null) {
							setNewDate(value);
						}
					}}
				/>
				{type === DetailsType.Note ? (
					<TextField
						sx={{ m: 1, width: "120px" }}
						size="small"
						value={newAssignees[0]}
						onChange={(e) => setNewAssignees([e.target.value])}
					/>
				) : (
					<Select
						sx={{ m: 1, width: "120px" }}
						size="small"
						inputProps={{
							focused: isAssigneeEdited,
						}}
						value={newAssignees}
						onChange={(e) =>
							typeof e.target.value === "string"
								? setNewAssignees([e.target.value])
								: setNewAssignees(e.target.value)
						}
						renderValue={(selected) => (
							<>
								{selected
									.map(
										(value) =>
											people.find(
												({ _id }) => _id === value
											)?.name || value
									)
									.join(", ")}
							</>
						)}
						multiple
					>
						{newAssignees
							.filter(
								(assignee) =>
									people.find(
										({ _id }) => _id === assignee
									) === undefined
							)
							.map((assignee) => (
								<MenuItem key={assignee} value={assignee}>
									<ListItemText
										primary={assignee}
										sx={{ fontStyle: "italic" }}
									/>
								</MenuItem>
							))}
						{people
							.filter(({ archived }) => !archived)
							.map((v) => (
								<MenuItem key={v._id} value={v._id}>
									<ListItemText primary={v.name} />
								</MenuItem>
							))}
					</Select>
				)}
				<TextField
					size="small"
					sx={{ flex: 1, m: 1 }}
					value={newDescription}
					multiline
					onChange={(e) => setNewDescription(e.target.value)}
				/>
				<Box>
					<Tooltip title="Save Changes">
						<IconButton
							sx={{ m: 1, marginRight: 0, marginLeft: 0 }}
							disabled={!isEdited}
							onClick={() =>
								updateDetail(
									detail._id,
									newDate,
									newAssignees,
									newDescription
								)
							}
						>
							<Save />
						</IconButton>
					</Tooltip>
					{isEdited ? (
						<Tooltip title="Undo Changes">
							<IconButton
								sx={{ m: 1, marginLeft: 0 }}
								onClick={() => {
									setNewDate(dayjs(detail.date));
									setNewAssignees(detail.assignees);
									setNewDescription(detail.description);
								}}
							>
								<Restore />
							</IconButton>
						</Tooltip>
					) : (
						<Tooltip title="Delete">
							<IconButton
								sx={{ m: 1, marginLeft: 0 }}
								onClick={() => deleteDetail(detail._id)}
							>
								<Delete />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>
			{notificationsOpen && (
				<DetailsNotifications
					setRemindersEdited={setRemindersEdited}
					declareEdited={declareEdited}
					detail={detail}
					sessionState={sessionState}
					setSessionState={setSessionState}
					setPostStatus={setPostStatus}
				/>
			)}
		</>
	);
}

interface DetailsGridProps {
	people: Person[];
	type: DetailsType;
	id: string;
	sessionState: SessionState;
	saveKeyPressed: boolean;
	setSessionState: (arg0: SessionState) => void;
	setPostStatus: (arg0: QueryStatus) => void;
	declareEdited: (arg0: string, arg1: boolean) => void;
	defaultDirection: "up" | "down";
	clientName: string;
	attorneys: string[];
	staffs: string[];
}

export function DetailsGrid({
	people,
	type,
	id,
	sessionState,
	setSessionState,
	setPostStatus,
	declareEdited,
	defaultDirection,
	saveKeyPressed,
	clientName,
	attorneys,
	staffs,
}: DetailsGridProps) {
	const [notesTerm, setNotesTerm] = useState("");
	const [addDetailDialog, setAddDetailDialog] = useState<DetailsType | null>(
		null
	);
	const [deleteDialog, setDeleteDialog] = useState<string | null>(null);
	const [direction, setDirection] = useState(defaultDirection);

	const [date, setDate] = useState<null | Dayjs>(null);
	const [assignees, setAssignees] = useState<string[]>([]);
	const [description, setDescription] = useState("");

	const detailsQuery = useQuery({
		queryKey: ["details", type, id],
		queryFn: () =>
			getDetails(
				{
					archived: false,
					caseId: id,
					type: {
						[DetailsType.Note]: "note",
						[DetailsType.Task]: "task",
						[DetailsType.Deadline]: "deadline",
					}[type],
				},
				sessionState,
				setSessionState
			),
		enabled: true,
	});

	useEffect(() => {
		if (sessionState.status === SessionStatus.LOGGED_IN) {
			detailsQuery.refetch();
		}
	}, [sessionState]);

	const rawDetailList: Detail[] = (detailsQuery.data || [])
		.map((detail: Detail & { assignee: string }) => ({
			...detail,
			assignee: undefined,
			assignees:
				detail.assignees === undefined
					? [detail.assignee]
					: detail.assignees,
		}))
		.filter(({ description }: { description: string }) =>
			description.toLowerCase().includes(notesTerm.toLowerCase())
		)
		.toSorted((a: { date: string }, b: { date: string }) =>
			direction === "down"
				? -dayjs(a.date).diff(dayjs(b.date))
				: dayjs(a.date).diff(dayjs(b.date))
		);

	const currentDetails = rawDetailList.filter(
		({ date }) => dayjs(date).diff(dayjs(), "days") >= -1
	);
	const passedDetails = rawDetailList.filter(
		({ date }) => dayjs(date).diff(dayjs(), "days") < -1
	);

	const createDetailRow = (detail: Detail, isLast: boolean) => (
		<DetailsRow
			people={people}
			saveKeyPressed={saveKeyPressed}
			declareEdited={declareEdited}
			isLast={isLast}
			key={detail._id}
			type={type}
			detail={detail}
			deleteDetail={(deleteId) => setDeleteDialog(deleteId)}
			updateDetail={(detailId, newDate, newAssignees, newDescription) => {
				setPostStatus(QueryStatus.LOADING);
				postAction(
					{
						action: "update-detail",
						id: detailId,
						data: {
							date: newDate,
							assignees: newAssignees,
							description: newDescription,
						},
						emails: createDetailEmails(
							assignees,
							attorneys,
							staffs,
							people,
							clientName,
							description,
							addDetailDialog as DetailsType,
							date
						),
					},
					sessionState,
					setSessionState,
					() => {
						setPostStatus(QueryStatus.SUCCESS);
						detailsQuery.refetch();
					},
					(e) => {
						console.error(e);
						setPostStatus(QueryStatus.FAILURE);
					}
				);
			}}
			setIsComplete={(detailId, value) => {
				setPostStatus(QueryStatus.LOADING);
				postAction(
					{
						action: "update-detail-complete",
						id: detailId,
						isComplete: value,
					},
					sessionState,
					setSessionState,
					() => {
						setPostStatus(QueryStatus.SUCCESS);
						detailsQuery.refetch();
					},
					(e) => {
						console.error(e);
						setPostStatus(QueryStatus.FAILURE);
					}
				);
			}}
			sessionState={sessionState}
			setSessionState={setSessionState}
			setPostStatus={setPostStatus}
		/>
	);

	return (
		<>
			<Box sx={{ display: "flex", marginBottom: "5px" }}>
				<ToggleButtonGroup
					sx={{ marginRight: "5px" }}
					size="small"
					value={direction}
					onChange={(_, value) => setDirection(value)}
					exclusive
				>
					<Tooltip title="Sort Descending">
						<ToggleButton value="down">
							<ArrowDownward />
						</ToggleButton>
					</Tooltip>
					<Tooltip title="Sort Ascending">
						<ToggleButton value="up">
							<ArrowUpward />
						</ToggleButton>
					</Tooltip>
				</ToggleButtonGroup>
				<TextField
					size="small"
					sx={{ flexGrow: 1, marginRight: "5px" }}
					label={`Search ${
						{
							[DetailsType.Note]: "notes",
							[DetailsType.Task]: "tasks",
							[DetailsType.Deadline]: "deadlines",
						}[type]
					}...`}
					value={notesTerm}
					onChange={(e) => setNotesTerm(e.target.value)}
				></TextField>
				<Button
					variant="outlined"
					onClick={() => {
						setAddDetailDialog(type);
						if (type === DetailsType.Note) {
							setDate(dayjs());
						}
					}}
				>
					Add{" "}
					{
						{
							[DetailsType.Note]: "Note",
							[DetailsType.Task]: "Task",
							[DetailsType.Deadline]: "Deadline",
						}[type]
					}
				</Button>
			</Box>
			<Box
				sx={{
					borderRadius: "4px",
					border: (theme) => `1px solid ${theme.palette.grey[800]}`,
					height: "calc(100% - 80px)",
				}}
			>
				<Box
					sx={{
						height: "100%",
					}}
				>
					<Box
						sx={{
							display: "flex",
							height: "40px",
							width: "100%",
							borderBottom: (theme) =>
								`1px solid ${theme.palette.grey[800]}`,
							zIndex: 99,
							background: (theme) =>
								theme.palette.background.paper,
						}}
					>
						{[DetailsType.Deadline, DetailsType.Task].includes(
							type
						) && (
							<Box
								sx={{
									width: "82px",
									padding: 1,
									background: (theme) =>
										`repeating-linear-gradient(45deg, ${theme.palette.grey[800]}, ${theme.palette.grey[800]} 10px, ${theme.palette.grey[900]} 10px, ${theme.palette.grey[900]} 20px)`,
								}}
							/>
						)}
						<Box
							sx={{
								width: "219px",
								padding: 1,
								borderRight: (theme) =>
									`1px dashed ${theme.palette.grey[800]}`,
							}}
						>
							<Typography
								sx={{
									color: (theme) =>
										theme.palette.text.secondary,
								}}
							>
								{type === DetailsType.Task
									? "Task Due"
									: "Date"}
							</Typography>
						</Box>
						<Box
							sx={{
								width: "119px",
								padding: 1,
								borderRight: (theme) =>
									`1px dashed ${theme.palette.grey[800]}`,
							}}
						>
							<Typography
								sx={{
									color: (theme) =>
										theme.palette.text.secondary,
								}}
							>
								Assignee
							</Typography>
						</Box>
						<Box
							sx={{
								padding: 1,
							}}
						>
							<Typography
								sx={{
									color: (theme) =>
										theme.palette.text.secondary,
								}}
							>
								Description
							</Typography>
						</Box>
					</Box>
					<Box sx={{ overflow: "auto", height: "calc(100% - 40px)" }}>
						{currentDetails.length + passedDetails.length === 0 ? (
							<Typography
								sx={{
									color: (theme) =>
										theme.palette.text.secondary,
									marginTop: "40px",
									display: "flex",
									justifyContent: "center",
								}}
							>
								No{" "}
								{
									{
										[DetailsType.Note]: "notes",
										[DetailsType.Task]: "tasks",
										[DetailsType.Deadline]: "deadlines",
									}[type]
								}
							</Typography>
						) : (
							<>
								{type === DetailsType.Deadline ? (
									<>
										{currentDetails.map((detail, i) =>
											createDetailRow(
												detail,
												i === currentDetails.length - 1
											)
										)}
										{passedDetails.length > 0 && (
											<>
												<Divider
													sx={{
														marginTop: "10px",
														marginBottom: "10px",
														color: (theme) =>
															theme.palette.text
																.disabled,
													}}
												>
													Passed Deadlines
												</Divider>
												{passedDetails.map(
													(detail, i) =>
														createDetailRow(
															detail,
															i ===
																passedDetails.length -
																	1
														)
												)}
											</>
										)}
									</>
								) : (
									<>
										{rawDetailList.map((detail, i) =>
											createDetailRow(
												detail,
												i === rawDetailList.length - 1
											)
										)}
									</>
								)}
							</>
						)}
					</Box>
				</Box>
			</Box>
			<Dialog
				open={addDetailDialog !== null}
				onClose={() => setAddDetailDialog(null)}
			>
				<DialogTitle>Add Detail</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							justifyContent: "center",
						}}
					>
						<FormControl sx={{ m: 1, width: 240 }}>
							<DateTimePicker
								// disabled={[DetailsType.Note].includes(addDetailDialog as DetailsType)}
								label={
									[DetailsType.Task].includes(
										addDetailDialog as DetailsType
									)
										? "Task Due"
										: "Date"
								}
								value={date}
								onChange={setDate}
							/>
						</FormControl>
						<FormControl sx={{ m: 1, width: 240 }}>
							{addDetailDialog === DetailsType.Note ? (
								<TextField
									label="Assignee"
									value={assignees[0]}
									onChange={(e) =>
										setAssignees([e.target.value])
									}
								/>
							) : (
								<Select
									label="Assignee(s)"
									value={assignees}
									onChange={(e) =>
										typeof e.target.value === "string"
											? setAssignees([e.target.value])
											: setAssignees(e.target.value)
									}
									renderValue={(selected) => (
										<>
											{selected
												.map(
													(value) =>
														people.find(
															({ _id }) =>
																_id === value
														)?.name || value
												)
												.join(", ")}
										</>
									)}
									multiple
								>
									{people
										.filter(({ archived }) => !archived)
										.map((v) => (
											<MenuItem key={v._id} value={v._id}>
												<ListItemText
													primary={v.name}
												/>
											</MenuItem>
										))}
								</Select>
							)}
						</FormControl>
						<FormControl sx={{ m: 1, width: 540 }}>
							<TextField
								label="Description"
								variant="filled"
								multiline
								value={description}
								onChange={(e) => setDescription(e.target.value)}
							></TextField>
						</FormControl>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setAddDetailDialog(null);
							setDescription("");
							setAssignees([]);
							setDate(dayjs());
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={
							{
								[DetailsType.Note]: false,
								[DetailsType.Task]:
									assignees.length === 0 ||
									assignees[0] === "",
								[DetailsType.Deadline]: false,
							}[addDetailDialog as DetailsType]
						}
						onClick={() => {
							setPostStatus(QueryStatus.LOADING);
							postAction(
								{
									action: "add-detail",
									id,
									data: {
										date,
										assignees,
										description,
										caseId: id,
										type: {
											[DetailsType.Note]: "note",
											[DetailsType.Task]: "task",
											[DetailsType.Deadline]: "deadline",
										}[addDetailDialog as DetailsType],
									},
									emails: createDetailEmails(
										assignees,
										attorneys,
										staffs,
										people,
										clientName,
										description,
										addDetailDialog as DetailsType,
										date
									),
								},
								sessionState,
								setSessionState,
								() => {
									setPostStatus(QueryStatus.SUCCESS);
									detailsQuery.refetch();
									setAddDetailDialog(null);
									setDescription("");
									setAssignees([]);
									setDate(dayjs());
								},
								(e) => {
									console.error(e);
									setPostStatus(QueryStatus.FAILURE);
								}
							);
						}}
					>
						Add
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={deleteDialog !== null}
				onClose={() => setDeleteDialog(null)}
			>
				<DialogTitle>
					{"Are you sure you want to archive this detail?"}
				</DialogTitle>
				<DialogActions>
					<Button onClick={() => setDeleteDialog(null)}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						onClick={() => {
							setPostStatus(QueryStatus.LOADING);
							postAction(
								{
									action: "delete-detail",
									id: deleteDialog,
								},
								sessionState,
								setSessionState,
								() => {
									setPostStatus(QueryStatus.SUCCESS);
									detailsQuery.refetch();
									setDeleteDialog(null);
								},
								(e) => {
									console.error(e);
									setPostStatus(QueryStatus.FAILURE);
								}
							);
						}}
					>
						Archive
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
